import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient, { registerChallengeHandler } from '../services/api';
import { clearAllOfflineData, purgeStaleData } from '../services/offlineQueue';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [challengeActive, setChallengeActive] = useState(false);

  // Register the challenge handler so the api.js interceptor can trigger it
  useEffect(() => {
    registerChallengeHandler(setChallengeActive);
  }, []);

  // Purge stale offline data on startup
  useEffect(() => {
    purgeStaleData().catch(() => {});
  }, []);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem('token');
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.user);
      setMustChangePassword(!!response.user?.must_change_password);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setUser(null);
      setMustChangePassword(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, turnstileToken) => {
    try {
      const response = await apiClient.post('/auth/login', {
        username,
        password,
        turnstileToken,
      });
      if (response.token) {
        localStorage.setItem('token', response.token);
        setUser(response.user);
        const mcp = !!response.must_change_password;
        setMustChangePassword(mcp);
        return { success: true, user: response.user, mustChangePassword: mcp };
      }
      return { success: false, message: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.message || 'Login failed',
        rateLimited: error.rateLimited || false,
      };
    }
  };

  const logout = useCallback(async () => {
    localStorage.removeItem('token');
    setUser(null);
    setMustChangePassword(false);
    setChallengeActive(false);

    // Clear all offline data on logout
    try {
      await clearAllOfflineData();
    } catch (err) {
      console.error('Failed to clear offline data on logout:', err);
    }

    window.location.href = '/login';
  }, []);

  const changePasswordComplete = (newToken) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
    }
    setMustChangePassword(false);
  };

  const handleChallengeVerified = useCallback(() => {
    setChallengeActive(false);
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    mustChangePassword,
    changePasswordComplete,
    challengeActive,
    setChallengeActive,
    handleChallengeVerified,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
